import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { RotateCcw } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { Button } from '@/shared/ui/buttons/Button';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { Textarea } from '@/shared/ui/inputs/Textarea';
import { extractErrorMessage } from '@/shared/api/errors';
import { useReturns, useReturnConfirm, useReturnReject, useReturnCancel } from '@/entities/returns';
import { usePerm } from '@/shared/hooks/usePerm';
import { useToast } from '@/shared/hooks/useToast';
import { formatUsd } from '@/shared/utils/currency';
import { formatDateTime } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';
import type {
  ApiReturnRequest,
  ReturnStatus,
} from '@/shared/types/api/returns';

const STATUS_FILTERS: { value: ReturnStatus | undefined; labelKey: string }[] = [
  { value: undefined, labelKey: 'returns.filter.all' },
  { value: 'PENDING', labelKey: 'returns.filter.pending' },
  { value: 'CONFIRMED', labelKey: 'returns.filter.confirmed' },
  { value: 'REJECTED', labelKey: 'returns.filter.rejected' },
];

export const WidgetsReturnsMain = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const canConfirm = usePerm('returns.confirm');
  const canViewParcel = usePerm('parcels.detail');

  const [status, setStatus] = useState<ReturnStatus | undefined>('PENDING');
  const [cancelTarget, setCancelTarget] = useState<ApiReturnRequest | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<ApiReturnRequest | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ApiReturnRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const query = useReturns({ status });
  const rows = useMemo(() => query.data ?? [], [query.data]);

  const confirm = useReturnConfirm();
  const reject = useReturnReject();
  const cancel = useReturnCancel();

  const columns = useMemo<ColumnDef<ApiReturnRequest>[]>(
    () => [
      {
        accessorKey: 'parcel',
        header: t('returns.columns.parcel'),
        cell: (info) => {
          const row = info.row.original;
          if (!row.parcel_id || !canViewParcel) {
            return (
              <span className="block max-w-[200px] truncate font-medium text-ink">
                {row.parcel_display}
              </span>
            );
          }
          return (
            <button
              type="button"
              onClick={() => navigate(`/parcels/${row.parcel_id}`)}
              className="block max-w-[200px] truncate text-left font-medium text-brand-600 hover:underline"
            >
              {row.parcel_display}
            </button>
          );
        },
      },
      {
        accessorKey: 'note',
        header: t('returns.columns.note'),
        cell: (info) => (
          <span className="block max-w-[260px] truncate text-ink">
            {info.row.original.note}
          </span>
        ),
      },
      {
        accessorKey: 'fee',
        header: t('returns.columns.fee'),
        cell: (info) => formatUsd(Number(info.row.original.fee)),
      },
      {
        accessorKey: 'status',
        header: t('returns.columns.status'),
        cell: (info) => {
          const rowStatus = info.row.original.status;
          return (
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                rowStatus === 'CONFIRMED'
                  ? 'bg-emerald-100 text-emerald-700'
                  : rowStatus === 'REJECTED'
                    ? 'bg-rose-100 text-rose-700'
                    : rowStatus === 'CANCELLED'
                      ? 'bg-slate-100 text-slate-600'
                      : 'bg-amber-100 text-amber-700',
              )}
            >
              {rowStatus === 'CANCELLED'
                ? t('returns.status.cancelled')
                : info.row.original.status_label}
            </span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: t('returns.columns.createdAt'),
        cell: (info) => formatDateTime(info.row.original.created_at),
      },
      {
        id: 'actions',
        header: t('returns.columns.actions'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          if (!canConfirm) return null;
          const showActions = row.status === 'PENDING';
          const showCancel = row.status === 'PENDING' || row.status === 'CONFIRMED';
          if (!showActions && !showCancel) return null;
          return (
            <div className="flex gap-2">
              {showActions ? (
                <>
                  <Button
                    variant="success"
                    size="sm"
                    loading={confirm.isPending && confirm.variables === row.id}
                    onClick={() => setConfirmTarget(row)}
                  >
                    {t('returns.confirm')}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    loading={reject.isPending && reject.variables?.id === row.id}
                    onClick={() => {
                      setRejectReason('');
                      setRejectTarget(row);
                    }}
                  >
                    {t('returns.reject')}
                  </Button>
                </>
              ) : null}
              {showCancel ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setCancelTarget(row)}
                >
                  {t('returns.cancel')}
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [t, canConfirm, canViewParcel, confirm, reject, navigate],
  );

  return (
    <>
      <PageHeader title={t('returns.title')} subtitle={t('returns.subtitle')} />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <FilterBar hideSearch>
          {STATUS_FILTERS.map((filter) => {
            const isActive = status === filter.value;
            return (
              <Button
                key={filter.labelKey}
                variant={isActive ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setStatus(filter.value)}
              >
                {t(filter.labelKey)}
              </Button>
            );
          })}
        </FilterBar>

        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('returns.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            isLoading={query.isLoading}
            emptyTitle={t('returns.emptyTitle')}
            emptyDescription={t('returns.emptyDescription')}
            emptyIcon={<RotateCcw className="h-8 w-8" aria-hidden />}
            getRowId={(row) => String(row.id)}
            numbered
          />
        )}
      </div>

      <ConfirmDialog
        open={cancelTarget !== null}
        title={t('returns.cancelTitle')}
        description={
          cancelTarget
            ? t('returns.cancelDescription', { name: cancelTarget.parcel_display })
            : ''
        }
        size="md"
        confirmVariant="danger"
        confirmLabel={t('returns.cancelConfirm')}
        cancelLabel={t('common.cancel')}
        loading={cancel.isPending}
        onCancel={() => setCancelTarget(null)}
        onConfirm={() => {
          if (!cancelTarget) return;
          cancel.mutate(cancelTarget.id, {
            onSuccess: () => {
              toast.success(t('returns.toast.cancelled'));
              setCancelTarget(null);
            },
            onError: () => toast.error(t('common.errorGeneric')),
          });
        }}
      >
        <p className="text-sm text-ink-soft">
          {cancelTarget?.parcel_display}
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmTarget !== null}
        title={t('returns.confirmTitle')}
        description={
          confirmTarget
            ? t('returns.confirmDescription', { name: confirmTarget.parcel_display })
            : ''
        }
        confirmLabel={t('returns.confirmConfirm')}
        cancelLabel={t('common.cancel')}
        confirmVariant="success"
        loading={confirm.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => {
          if (!confirmTarget) return;
          confirm.mutate(confirmTarget.id, {
            onSuccess: () => {
              toast.success(t('returns.toast.confirmed'));
              setConfirmTarget(null);
            },
            onError: () => toast.error(t('common.errorGeneric')),
          });
        }}
      />

      <Dialog
        open={rejectTarget !== null}
        onClose={() => setRejectTarget(null)}
        title={t('returns.rejectTitle')}
        description={
          rejectTarget
            ? t('returns.rejectDescription', { name: rejectTarget.parcel_display })
            : ''
        }
        size="md"
        closeOnBackdrop={!reject.isPending}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setRejectTarget(null)}
              disabled={reject.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={reject.isPending}
              onClick={() => {
                if (!rejectTarget) return;
                const trimmed = rejectReason.trim();
                reject.mutate(
                  { id: rejectTarget.id, reject_reason: trimmed || undefined },
                  {
                    onSuccess: () => {
                      toast.success(t('returns.toast.rejected'));
                      setRejectTarget(null);
                    },
                    onError: () => toast.error(t('common.errorGeneric')),
                  },
                );
              }}
            >
              {t('returns.rejectConfirm')}
            </Button>
          </>
        }
      >
        <Textarea
          label={t('returns.rejectReasonLabel')}
          placeholder={t('returns.rejectReasonPlaceholder')}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Dialog>
    </>
  );
};
